import React, { useMemo, useState } from 'react';
import { useMemoryStore } from '../../stores/memory-store';
import type { RecentCapture } from '../../stores/memory-store';
import { PageHeader } from '../shared-ui/PageHeader';

/**
 * Memory view: the PA's knowledge surface. Search, filter, and browse the
 * captures Signa has stored. This is a trust surface: users must see (and
 * eventually edit/forget) what the PA remembers.
 *
 * R4 scope: search + filter pills + source-badged cards with Edit/Forget
 * action buttons. Forget routes through the store's local removal (backend
 * "forget" endpoint lands in a future sprint).
 */

type Filter = 'all' | 'people' | 'meetings' | 'standalone';

export function MemoryView() {
  const recent = useMemoryStore((s) => s.recentCaptures);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let pool = recent;
    if (filter === 'people') {
      pool = pool.filter((c) => !!(c.contextSender || c.contextSubject));
    } else if (filter === 'meetings') {
      pool = pool.filter((c) => c.type === 'meeting_note');
    } else if (filter === 'standalone') {
      pool = pool.filter((c) => !c.contextSender && !c.contextSubject);
    }
    if (q) {
      pool = pool.filter(
        (c) =>
          c.content.toLowerCase().includes(q) ||
          (c.contextSender ?? '').toLowerCase().includes(q) ||
          (c.contextSubject ?? '').toLowerCase().includes(q)
      );
    }
    return [...pool].sort(
      (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
    );
  }, [recent, filter, query]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '40px 36px 120px',
          width: '100%',
        }}
      >
        <PageHeader
          title="Memory"
          subtitle="What Signa remembers for you."
        />

        <SearchInput value={query} onChange={setQuery} />

        <div
          style={{
            display: 'flex',
            gap: 6,
            margin: '20px 0 28px',
            flexWrap: 'wrap',
          }}
        >
          {(['all', 'people', 'meetings', 'standalone'] as Filter[]).map((f) => (
            <FilterPill
              key={f}
              active={filter === f}
              label={f}
              onClick={() => setFilter(f)}
            />
          ))}
        </div>

        {filtered.length === 0 ? (
          <EmptyState query={query} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map((c) => (
              <MemoryCard key={c.id} capture={c} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Search + filter pill

function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingBottom: 6,
        borderBottom: `1px solid ${focused ? 'var(--accent)' : 'var(--rule)'}`,
        transition: 'border-color 150ms ease',
      }}
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 16 16"
        fill="none"
        stroke="var(--ink-faint)"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="7" cy="7" r="4.5" />
        <path d="m10.3 10.3 3 3" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Search memory..."
        style={{
          flex: 1,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--ink)',
          fontFamily: 'var(--font-ui)',
          fontSize: 15,
          padding: '10px 0',
        }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--ink-faint)',
            fontSize: 13,
            padding: '2px 8px',
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

function FilterPill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: '5px 12px',
        fontFamily: 'var(--font-ui)',
        fontSize: 12.5,
        fontWeight: active ? 500 : 400,
        color: active ? 'var(--accent)' : 'var(--ink-soft)',
        background: active ? 'var(--accent-wash)' : 'transparent',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--rule)'}`,
        borderRadius: 16,
        cursor: 'pointer',
        textTransform: 'capitalize',
      }}
    >
      {label}
    </button>
  );
}

// Memory card

function MemoryCard({ capture }: { capture: RecentCapture }) {
  const [hover, setHover] = useState(false);
  const badge = badgeFor(capture);

  const onForget = () => {
    // Local-only for now; a dedicated backend "forget" endpoint lands later.
    useMemoryStore.setState((state) => ({
      recentCaptures: state.recentCaptures.filter((c) => c.id !== capture.id),
    }));
  };

  return (
    <article
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '14px 16px',
        background: 'var(--card)',
        border: '1px solid var(--rule)',
        borderRadius: 12,
        fontFamily: 'var(--font-ui)',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            padding: '2px 6px',
            background: 'var(--accent-wash)',
            borderRadius: 3,
          }}
        >
          {badge}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            color: 'var(--ink-faint)',
            letterSpacing: '0.5px',
          }}
        >
          {formatDate(capture.savedAt)}
        </span>
      </div>
      <div style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.6 }}>
        {capture.content}
      </div>
      {capture.contextSender && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: 'var(--ink-faint)',
          }}
        >
          re: {capture.contextSender}
          {capture.contextSubject ? ` - ${capture.contextSubject}` : ''}
        </div>
      )}
      {hover && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            gap: 4,
          }}
        >
          <CardAction label="Edit" onClick={() => {}} />
          <CardAction label="Forget" onClick={onForget} danger />
        </div>
      )}
    </article>
  );
}

function CardAction({
  label,
  onClick,
  danger = false,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '3px 9px',
        background: 'var(--paper)',
        border: '1px solid var(--rule)',
        color: 'var(--ink-soft)',
        fontFamily: 'var(--font-ui)',
        fontSize: 11.5,
        borderRadius: 5,
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = danger ? 'var(--error)' : 'var(--accent)';
        e.currentTarget.style.borderColor = danger ? 'var(--error)' : 'var(--accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--ink-soft)';
        e.currentTarget.style.borderColor = 'var(--rule)';
      }}
    >
      {label}
    </button>
  );
}

function badgeFor(capture: RecentCapture): string {
  if (capture.contextSender || capture.contextSubject) return 'email';
  if (capture.type === 'meeting_note') return 'meeting';
  if (capture.type === 'commitment') return 'commitment';
  if (capture.type === 'insight') return 'insight';
  return 'note';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function EmptyState({ query }: { query: string }) {
  return (
    <div
      style={{
        padding: '60px 24px',
        textAlign: 'center',
        color: 'var(--ink-faint)',
        fontSize: 13,
        border: '1px dashed var(--rule)',
        borderRadius: 12,
      }}
    >
      {query
        ? `Nothing matches "${query}".`
        : 'Nothing captured yet. Signa stores context as you work: meeting notes, commitments, insights.'}
    </div>
  );
}
